import axios from 'axios';
import { atom } from 'jotai';
import { getDefaultStore } from 'jotai';

export const serverVersionAtom = atom(null);

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const store = getDefaultStore();

const instance = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

instance.interceptors.request.use(
    config => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

const clearSessionAndRedirect = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('username');
    localStorage.removeItem('authOrigin');
    window.location.href = '/login';
};

instance.interceptors.response.use(
    response => {
        const apiVersion = response.headers['x-api-version'];
        if (apiVersion) store.set(serverVersionAtom, apiVersion);
        return response;
    },
    async error => {
        const originalRequest = error.config;

        if (
            error.response?.data?.error === 'jwt expired' &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true;

            const storedRefreshToken = localStorage.getItem('refreshToken');
            const username = localStorage.getItem('username');

            if (!storedRefreshToken) {
                clearSessionAndRedirect();
                return Promise.reject(error);
            }

            try {
                const authOrigin = localStorage.getItem('authOrigin');
                let AccessToken, IdToken;

                if (authOrigin === 'cognito-oauth') {
                    // Google OAuth: refresh directly against Cognito's token endpoint
                    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
                    const clientId = process.env.REACT_APP_COGNITO_WEB_CLIENT_ID;

                    const body = new URLSearchParams({
                        grant_type: 'refresh_token',
                        client_id: clientId,
                        refresh_token: storedRefreshToken,
                    });

                    const refreshResponse = await fetch(`${domain}/oauth2/token`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: body.toString(),
                    });

                    if (!refreshResponse.ok) {
                        throw new Error('OAuth token refresh failed');
                    }

                    const tokens = await refreshResponse.json();
                    AccessToken = tokens.id_token;
                    IdToken = tokens.id_token;
                } else if (authOrigin === 'apple') {
                    // Apple social auth: our own JWT + DB-stored refresh token
                    const response = await axios.post(`${BASE_URL}/auth/refresh`, {
                        refreshToken: storedRefreshToken,
                        type: 'social',
                    });
                    AccessToken = response.data.AccessToken;
                    IdToken = response.data.IdToken;
                } else if (username) {
                    // Password users: refresh via BE (server-side Cognito client)
                    const response = await axios.post(`${BASE_URL}/auth/refresh`, {
                        refreshToken: storedRefreshToken,
                        username,
                    });
                    AccessToken = response.data.AccessToken;
                    IdToken = response.data.IdToken;
                } else {
                    const response = await axios.post(`${BASE_URL}/auth/refresh`, {
                        refreshToken: storedRefreshToken,
                        type: 'social',
                    });
                    AccessToken = response.data.AccessToken;
                    IdToken = response.data.IdToken;
                }

                localStorage.setItem('accessToken', AccessToken);
                localStorage.setItem('idToken', IdToken);

                originalRequest.headers['Authorization'] = `Bearer ${AccessToken}`;
                return instance(originalRequest);
            } catch (refreshError) {
                clearSessionAndRedirect();
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default instance;
